export const InfoLogs = {
  adyen3dsPaymentSuccess: 'Adyen 3DS payment success',
  // ACDEV-26410: emitted from the SDK's `onActionHandled` the moment the 3DS2
  // challenge iframe paints. Pair it with `adyen3dsPaymentSuccess` /
  // `adyen3dsPaymentError` to split the challenge drop-off into "the iframe
  // never rendered" (technical) vs "it rendered and the shopper abandoned"
  // (behavioral) — the two were indistinguishable before this log existed.
  adyen3dsChallengePresented: 'Adyen 3DS challenge presented',
  // The denominator for the whole challenge funnel. Without a log at mount
  // time "the iframe never painted" and "no challenge was ever attempted" are
  // the same absence of data. `actionSubtype` separates the frictionless
  // fingerprint (IdentifyShopper) from a real shopper-facing challenge, which
  // have very different expected completion rates.
  adyen3dsChallengeMounted: 'Adyen 3DS challenge mounted',
  // The step between "the server returned an action" and "a challenge exists".
  // It is what makes the gap attributable: reaching this and not mounting means
  // the component failed to come up, while never reaching it means the finalize
  // response was routed down another branch (redirect, or a method outside the
  // Adyen action path) and no challenge was ever going to appear.
  adyen3dsHandoffRequested: 'Adyen 3DS handoff requested',
  // The order-poll watchdog beat the challenge callbacks to the outcome. This
  // is the app-switch recovery working: the AUTHORISATION webhook settled the
  // order while the challenge result never travelled back through the iframe
  // (page backgrounded for an authentication app, result postMessage lost, or
  // the /payment/status race). Volume here measures how many sessions the
  // watchdog rescued that previously died as ACDEV-26410 dead sessions.
  adyen3dsSettledByOrderPoll: 'Adyen 3DS settled by order poll',
  // A challenge picked back up on a page that is not the one it started on.
  // Reaching this means the previous page died mid-authentication without ever
  // settling — Spanish app-based SCA backgrounds the tab for the whole
  // challenge and Chrome discards it, which is 36 `pagehide` events during a
  // challenge in Spain against 1 in France. Its volume is the size of the
  // cohort that was previously lost in silence; pairing it with the outcome
  // that follows is what says whether resuming them is worth anything.
  adyen3dsChallengeResumed: 'Adyen 3DS challenge resumed after page loss',
  // The challenge was destroyed by React while the payment was still pending.
  // This is the exit that had no instrumentation, and it is where the silent
  // cohort goes: of the challenges that never settle, 63% emitted heartbeats
  // and not one page lifecycle event — the page was alive, unfrozen and never
  // even backgrounded — yet the beats stop inside 30s for 72% of them. Nothing
  // but this teardown ends a heartbeat early, and it takes the Adyen
  // component, the order poll and the heartbeat with it, so the payment simply
  // stops existing in the data. `reason` splits the two cleanups that mean
  // opposite things: `unmounted` is a dead payment, `deps_changed` is a
  // challenge that gets rebuilt and re-sends its CReq.
  adyen3dsChallengeTornDown: 'Adyen 3DS challenge torn down before settling',
  // The counterpart, and the only way to tell the above was actually fixed
  // rather than merely quieter. A method change during a live challenge used
  // to close the 3DS form and kill the payment with it; now it is skipped, and
  // each of these is one challenge that would previously have been lost. Read
  // it as the fix's yield — if it stays at zero, the teardowns were coming
  // from somewhere else and `reason` above says where.
  //
  // Not Adyen-specific, and deliberately not named as though it were: the
  // reset closes whichever 3DS form is open, and the first one this caught in
  // production was a Forter/Stripe challenge seven seconds in. `provider`
  // separates them, because folding other providers into the Adyen count would
  // overstate the fix being measured.
  threeDSChallengeKeptThroughReset: '3DS challenge kept through a state reset',
  // The discriminator for the app-switch hypothesis. A mobile 3DS session
  // that goes dark ~2.5s after the challenge paints can mean two different
  // things: the shopper LEFT (app-switched to their bank app — this log
  // fires on visibility->hidden and usually flushes before the OS kills the
  // webview) or the page died under them with no user action (no log at
  // all). `msSinceChallengeMounted` matching the observed ~2.5s darkness
  // median is the direct confirmation that shoppers are leaving, not
  // crashing.
  adyen3dsChallengeBackgrounded: 'Adyen 3DS challenge backgrounded',
  // The other half of the funnel: of those who left, how many come back,
  // and after how long. Paired with `adyen3dsSettledByOrderPoll` this
  // separates "returned and the webhook had settled it" from "returned to a
  // still-pending challenge" from "never returned".
  adyen3dsChallengeForegrounded: 'Adyen 3DS challenge foregrounded',
  // Liveness beat, every 3s while a challenge is open (see
  // `startLivenessHeartbeat`). This is the instrument that settles the question
  // the backgrounded/foregrounded pair above cannot: those only fire if the
  // browser gets to run our handler, and a process kill never does. Beats are
  // delivered incrementally, so the LAST beat that arrives dates the moment the
  // page stopped executing.
  //   - beats stop early, no lifecycle marker  → killed without warning
  //   - beats stop right after a `pagehide`/`freeze` marker → the browser
  //     discarded or froze the page on its way out
  //   - beats continue to the cap with no outcome → the page was alive the
  //     whole time and the challenge itself failed; abandonment, not a kill
  // `visibilityState` per beat keeps throttling (~1/min while hidden) from
  // being misread as death.
  adyen3dsChallengeHeartbeat: 'Adyen 3DS challenge heartbeat',
  // Browser-announced end of the page while a payment is in flight: `pagehide`
  // (with `persisted`), `freeze`, `resume`. Fires before execution stops, so it
  // is the only self-reported cause of death available — and its absence next to
  // a stopped heartbeat is itself the evidence for an unannounced kill.
  paymentPageLifecycle: 'Payment page lifecycle event',
  // Set by Chrome on a document restored after the tab was discarded — proof of
  // a browser-initiated kill that survives the kill. Only observable when a
  // shopper reopens the tab, which production suggests is rare, so it
  // corroborates the heartbeat evidence rather than measuring the cohort.
  checkoutTabRestoredAfterDiscard: 'Checkout tab restored after discard',
  // Closes the Google Pay blind spot. 70 production sessions authorized Google
  // Pay and then went dark within 330ms having never reached
  // `adyen3dsChallengeMounted` — 6x the cohort that dies on a painted
  // challenge — and nothing was logged between the two points, so the break was
  // unattributable. These bracket the finalize call that sits in that gap:
  //   - authorized, no `googlePayFinalizeRequested` → the SDK never delivered
  //     `onSubmit`, so the break is inside adyen-web
  //   - requested, never returned → the page stopped, or the call hung, while
  //     the native sheet held the screen
  //   - returned, no challenge mounted → the finalize response reached us and
  //     the handoff to the 3DS component is what broke
  // `googlePayFinalizeRequested` also carries the runtime environment snapshot
  // (device RAM, embedded browser) that the dying cohort can be tested against.
  googlePayFinalizeRequested: 'Google Pay finalize requested',
  googlePayFinalizeReturned: 'Google Pay finalize returned',
  forter3dsChallengeStarted: 'Forter 3DS challenge started',
  forter3dsChallengeSuccess: 'Forter 3DS challenge success',
  // ACDEV-27224: the fraud-token funnel (script tag → `ftr:tokenReady` →
  // consumed at pay-time), instrumented the same way as the Apple/Google Pay
  // funnels above — RUM previously had NO checkpoints between "we called
  // `initialize()`" and "the BE received `forterTokenCookie`", so an empty
  // cookie was unattributable: never initialized? script blocked? script slow?
  // event never fired? These bracket every hop so a missing cookie is
  // traceable to the specific gap instead of reconstructed after the fact from
  // RUM network-request timestamps.
  forterScriptLoadStarted: 'Forter script load started',
  forterScriptLoaded: 'Forter script loaded',
  forterTokenReceived: 'Forter token received',
  // Fires exactly once per payment, from `getForterToken()` — the literal
  // moment the value that ships to the BE as `forterTokenCookie` is read.
  // `forterTokenPresentAtPayTime` (Info) carries whether it came from the
  // service (script fired in time) or the cookie fallback (script fired, but
  // the service singleton had reset/reloaded); `forterTokenMissingAtPayTime`
  // (Warn) is the direct RUM-queryable signal for this ticket's bug, paired
  // with a diagnostics snapshot (`scriptLoaded`, `isInitialized`,
  // `msSinceScriptAppended`) that says WHY: never configured, script still in
  // flight, or loaded with no token yet.
  forterTokenPresentAtPayTime: 'Forter token present at pay time',
  forterTokenMissingAtPayTime: 'Forter token missing at pay time',
  // `VITE_FORTER_ID_TOKEN` unset — `initialize()` no-ops before ever touching
  // the DOM. Previously only a `console.warn`, invisible in prod RUM; every
  // other checkpoint in this funnel depends on this NOT firing.
  forterNotConfigured: 'Forter script not configured',
  // Fires (Warn) if neither `onload` nor `onerror` has landed
  // `SCRIPT_STALL_WATCHDOG_MS` after the tag was appended. Some blockers let
  // the request hang indefinitely instead of erroring, which previously read
  // identically to "still loading" forever — this distinguishes a genuinely
  // stalled/blocked load from the normal fast case.
  forterScriptLoadStalled: 'Forter script load stalled',
  // Fires when `ftr:tokenReady` finally arrives in a session where a payment
  // already went out with `forterTokenMissingAtPayTime` — i.e. this session
  // WOULD have shipped a real cookie had pay happened even slightly later.
  // Separates "this session was never going to get a token" from "the token
  // just lost the race," which `forterTokenMissingAtPayTime` alone can't.
  forterTokenArrivedAfterMiss: 'Forter token arrived after a missed pay attempt',
  // Fires from `usePaymentToServer`, the statement immediately before the
  // request body is handed to `apiClient.post()` — which `JSON.stringify`s
  // it straight into `fetch()` with no further mutation (see
  // `ApiClient.post`). This is the last point FE code controls before the
  // browser's network stack takes over, so a non-empty `tokenPresent` here
  // is direct proof the FE asked the browser to send a real token on THIS
  // finalize call — it says nothing about whether the BE received it (see
  // `forterTokenPresentAtPayTime` for that caveat). Deliberately NOT wired
  // into `usePciPaymentToServer` — that request body goes to the PCI
  // gateway and is kept out of any FE-side inspection. Carries
  // `paymentMethodId` (safe, internal, non-PII) as the per-request join
  // key — `orderId` isn't logged here since it's already a global RUM
  // label attached to every event automatically, this one included.
  // Also carries the raw `forterTokenCookie` value itself — it's Forter's
  // fraud-detection token, not a payment credential, and this same value
  // is already sent to the BE on this same request, so it's diffable
  // against what the BE actually received.
  forterTokenHandedToFetch: 'Forter token handed to fetch for finalize request',
  addOrChangeCardClicked: 'Add or change card clicked',
  checkoutPresented: 'Checkout presented',
  checkoutPaymentRedirected: 'Checkout payment redirected',
  checkoutPaymentStarted: 'Checkout payment started',
  // ACDEV-27856: `runPayment` is guarded by a synchronous ref
  // (`isRunPaymentInProgressRef`) so a second concurrent invocation while one
  // is already in flight — e.g. a rapid double-click on the card Pay button
  // before React re-renders `isInPaymentProcess` — is dropped as a no-op
  // instead of firing a second finalize. This fires when that guard blocks a
  // call, so a Coralogix spike here is directly attributable to the
  // double-submit race rather than showing up only as a mismatched
  // success/failure event pair.
  checkoutPaymentDuplicateBlocked: 'Checkout payment duplicate call blocked',
  checkoutPaymentProcessed: 'Checkout payment processed',
  checkoutPaymentSuccess: 'Checkout payment success',
  checkoutPaymentCancelled: 'Checkout payment cancelled',
  checkoutClosed: 'Checkout closed',
  // Conversion-diagnostics funnel (present → pay). Emitted as Coralogix RUM
  // logs; the segmentation/dimension fields ride in labels so they stay
  // individually queryable despite `stringifyCustomLogData`.
  expressWalletResolved: 'Express wallet resolved',
  // Per-wallet Apple Pay / Google Pay availability (emitted as Coralogix RUM
  // logs). `expressWalletReady` fires when a wallet becomes usable;
  // `expressWalletUnavailable` (logged at Warn) fires when it doesn't — with a
  // `was_ready`/`disappeared` label distinguishing "never loaded" from "was
  // shown, then vanished". See `trackExpressWalletAvailability`.
  expressWalletReady: 'Express wallet ready',
  expressWalletUnavailable: 'Express wallet unavailable',
  applePayLoaded: 'Apple Pay loaded',
  // Fired (at Warn severity) when an Apple Pay button takes >5s to become
  // ready/settle. Carries a per-phase timing breakdown + a human-readable
  // `reason` attributing the delay to the dominant phase, so a stuck Apple
  // Pay is diagnosable from RUM without repro.
  applePaySlowLoad: 'Apple Pay slow load',
  cardFormStarted: 'Card form started',
  cardFieldInvalid: 'Card field invalid',
  cardFormReadyToPay: 'Card form ready to pay',
  payButtonBlockedClick: 'Pay button blocked click',
  // Stripe.js loader (RUM). `stripeJsLoaded` fires on EVERY resolved load with a
  // `durationMs`/`totalMs` + `outcome` label:
  //   - fast_path              : stock loadStripe resolved (the "like before" path)
  //   - global_already_present : recovery found window.Stripe already defined
  //   - recovered              : instance built after a file reload
  // `stripeJsReloadStarted` (Warn) fires when the fast path failed/stalled and we
  // begin reloading the file, carrying the `reason` (timeout|load_error|resolved_null).
  // `stripeJsReloadAttemptFailed` (Warn) fires per failed re-injection with `attempt`.
  // `stripeScriptReloaded` fires once when a re-injection finally succeeds.
  stripeJsLoaded: 'Stripe.js loaded',
  stripeJsReloadStarted: 'Stripe.js load failed — reloading file',
  stripeJsReloadAttemptFailed: 'Stripe.js reload attempt failed',
  stripeScriptReloaded: 'Stripe.js reloaded after failed initial load',
  // Emitted (Warn) by useStripeInitialize when the loader settled WITHOUT an
  // instance (Stripe unreachable after every reload). Carries `paymentMethod` /
  // `isExpress` labels — the per-form dimension the loader itself can't log —
  // so the "stuck loading" funnel can be sliced by which flow gave up.
  stripeInitUnavailable: 'Stripe unavailable — payment form cannot initialize',
  // Fires once per provider+method when a payment method has no pspSubMidPk
  // and the checkout uses the top-level publisher key instead.
  pspKeyTopLevelFallback: 'Checkout PSP key fell back to top-level pks',
  // ACDEV-25067: fires (Warn) when the BE returns `isValid: false` for a
  // wallet-derived ZIP tax lookup (either `/calculate-estimated-tax` for a
  // US/CA-matched wallet or `/handle-default-tax` for the fallback path).
  // Distinct from `walletZipTaxError` (which is only thrown/BE-5xx cases) so
  // "BE said invalid" and "BE call blew up" are queryable separately in RUM.
  walletZipTaxInvalid: 'Wallet ZIP tax returned invalid',
  // ACDEV-25067: fires inside `applyWalletTaxToCheckout` when a captured
  // wallet tax result is committed to `CheckoutContext` (buyer's checkout
  // price + ZIP now come from the wallet). Includes the `source`
  // (`calculate` vs `default`) so the two commit paths — real-jurisdiction
  // flush vs "no valid tax existed → default is a strict upgrade" — are
  // queryable separately.
  walletZipTaxApplied: 'Wallet ZIP tax applied to checkout',
  // ACDEV-25067: fires inside `applyWalletTaxToCheckout` when a
  // `default`-source result is deliberately DROPPED because the checkout
  // already held a valid tax (the default isn't authoritative for the
  // buyer's jurisdiction). Paired with a fire-and-forget
  // `/calculate-estimated-tax` on the pre-open ZIP to re-sync the BE
  // session — see `walletZipTaxResyncFailed` for the failure counterpart.
  walletZipTaxSuppressed: 'Wallet ZIP tax suppressed — pre-open tax preserved',
  // Wallet-tax funnel (ACDEV-27914 incident: three Apple Pay finalizes went
  // out with no `estimatedTaxCalculation` and RUM held NOTHING between
  // "Express wallet ready" and "Checkout payment started" — `SKIPPED` /
  // `SAME_ZIP` exits and a shippingaddresschange that never fires are all
  // silent). These three logs make every hop attributable:
  //   - `walletShippingAddressReceived`: the wallet's shippingaddresschange
  //     callback fired, with the SHAPE of what the wallet handed us
  //     (`hasPostalCode`, `country`, `addressKeys`). Its absence before a
  //     confirm means the sheet never asked us for a total.
  //   - `walletZipTaxOutcome`: every non-throwing exit of `runWalletZipTax`
  //     (`status` + `reason` for `skipped`, `endpoint`/`source` for BE
  //     calls). `ERROR` keeps its own `walletZipTaxError`.
  //   - `walletConfirmTaxState`: at `onConfirm`, what finalize is about to
  //     carry — whether a wallet tax was captured, whether the checkout tax
  //     is valid, how many shippingaddresschange events preceded it, and the
  //     postal code/country on the confirm event's billing/shipping details.
  //     Warn when nothing was captured AND the checkout tax is invalid: that
  //     is the exact state psps rejects with `estimatedTaxCalculation isNotEmpty`.
  walletShippingAddressReceived: 'Wallet shipping address received',
  walletZipTaxOutcome: 'Wallet ZIP tax outcome',
  walletConfirmTaxState: 'Wallet confirm tax state',
  // Google Pay sheet-callback funnel (CALLBACK_TIMED_OUT diagnostics).
  // Google gives the page ~30s to answer `onPaymentAuthorized` /
  // `onPaymentDataChanged`; production shows sheets dying with
  // CALLBACK_TIMED_OUT and ZERO merchant activity in between. These logs
  // instrument every hop so the break point is attributable from RUM:
  //   - `googlePayAuthorized`: the SDK delivered `onAuthorized` to our code —
  //     proves the Google→page callback bridge worked. Its ABSENCE after a
  //     click is the fingerprint of the Android OEM/in-app-browser bridge
  //     failure.
  //   - `googlePaySheetSettled`: our code resolved/rejected the sheet's
  //     SubmitActions (via dismiss / complete), with the outcome and
  //     ms-since-click.
  googlePayAuthorized: 'Google Pay payment authorized',
  googlePaySheetSettled: 'Google Pay sheet settled',
  // Fired at Warn severity ~35s after a Google Pay button click when the
  // session reached no terminal state. `sheet_phase` says where it stopped:
  //   - `clicked`: sheet opened but `onAuthorized` never arrived — either an
  //     idle shopper OR the Google callback bridge never delivered the event
  //     (the CALLBACK_TIMED_OUT cohort).
  //   - `authorized`: `onAuthorized` fired but `onSubmit` never ran — SDK
  //     chain stalled between the two.
  //   - `submitted`: `runPayment` ran but nothing settled the SubmitActions —
  //     a response branch that doesn't resolve the sheet.
  // `actions_pending` disambiguates: true means the sheet promise is still
  // unsettled and Google WILL show CALLBACK_TIMED_OUT.
  googlePaySheetStalled: 'Google Pay sheet stalled without terminal state',
  // Fired at Warn severity when the wallet ZIP-tax `onPaymentDataChanged`
  // handler is still awaiting the BE tax response close to Google's ~30s
  // callback budget — the pre-authorization variant of CALLBACK_TIMED_OUT.
  walletZipTaxSlow: 'Wallet ZIP tax exceeding Google callback budget',
};

export const ErrorLogs = {
  // Emitted for EVERY Adyen express-wallet error (Apple Pay & Google Pay),
  // including silent cancels — see useAdyenExpressError. `paymentMethod` in the
  // labels distinguishes the wallet.
  expressPaymentFailed: 'Express payment failed',
  adyen3dsPaymentError: 'Adyen 3DS payment error',
  // ACDEV-26410: the 3DS2 component's own failures — a missing `paymentData` /
  // `authorisationToken` on the action, or an unusable challenge. The SDK
  // surfaces these through `onError`, which was previously unwired, so they
  // were swallowed and the shopper just sat on a blank modal.
  adyen3dsComponentError: 'Adyen 3DS component error',
  // The `/payment/status` POST that submits the challenge result threw
  // (network drop, 5xx, abort). The call had no error handling, so the
  // rejection escaped `onAdditionalDetails` unhandled: the modal stayed up,
  // the wallet sheet never settled, and the order died on expiry with no
  // trace — indistinguishable in RUM from a shopper who walked away.
  adyen3dsStatusError: 'Adyen 3DS status request failed',
  // The challenge component never came back: `createFromAction` swallowed a
  // throw and returned null, the SDK was never initialized, or setup itself
  // rejected. Nothing mounts, no callback ever fires, and the shopper was
  // left on an empty modal until the order expired — silently, since the
  // failure happened before any of the challenge callbacks existed.
  adyen3dsChallengeUnavailable: 'Adyen 3DS challenge unavailable',
  forter3dsChallengeError: 'Forter 3DS challenge error',
  // ACDEV-27224: the `<script>` tag's `onerror` fired — blocked (ad-blocker /
  // extension / CSP), network failure, or 404. Carries `loadDurationMs` since
  // append so a fast failure (blocked outright) reads differently from a slow
  // one (timed out).
  forterScriptLoadFailed: 'Forter script failed to load',
  checkoutPaymentError: 'Checkout payment error',
  // Fired specifically when a decline is a velocity/rate-limit reject
  // (CardVelocityExceeded / WithdrawalCountLimitExceeded). Kept distinct from
  // the generic `checkoutPaymentError` so a spike in "too many attempts"
  // declines is queryable in Coralogix RUM on its own — these are recoverable
  // (wait & retry or switch method), not a hard card failure.
  velocityDecline: 'Checkout velocity decline',
  expressCheckoutError: 'Express checkout error',
  checkoutPaymentMethodNotAvailable: 'Checkout payment method not available',
  nuveiCardFormError: 'Nuvei card form error',
  braintreeCardFormError: 'Braintree card form error',
  adyenCardFormError: 'Adyen card form error',
  stripeCardFormError: 'Stripe card form error',
  appchargePaymentElementCardFormError: 'Appcharge payment element card form error',
  appchargePaymentElementPaymentFailed: 'Appcharge payment element payment failed',
  // Stripe.js could not be loaded even after re-injecting the file — the
  // terminal state behind an unrecoverable "loading Stripe" hang.
  stripeScriptLoadFailed: 'Stripe.js failed to load after reload attempts',
  // ACDEV-25067: any throw inside `runWalletZipTax` — from the BE call
  // (`/calculate-estimated-tax` or `/handle-default-tax`) or from the sync
  // pre-work (predicate check, ZIP normalization). Payment MUST NOT break
  // when this fires — callers see `{ status: ERROR }` and route to a
  // safe fallback (resolve the sheet with the current total).
  walletZipTaxError: 'Wallet ZIP tax calculation failed',
  // ACDEV-25067: fires when a wallet sheet callback body itself throws
  // (e.g. `elements.update()`, `event.resolve()`, an Adyen `resolve()`).
  // Distinct from `walletZipTaxError` because the failure is in the sheet
  // integration path, not the BE lookup — a spike here points at the wallet
  // SDK / DOM state, not our tax endpoints.
  walletZipTaxHandlerError: 'Wallet shipping-address handler threw',
  // ACDEV-25067: fires when the post-cancel `/calculate-estimated-tax`
  // recovery call (issued after a wallet flow's `/handle-default-tax`
  // dirtied the BE session) fails. Was previously swallowed. A spike here
  // correlated with wallet-cancel volume signals the BE session may still
  // hold the wallet-flow default tax when the buyer's next `runPayment`
  // fires — finalize will charge against that stale default until the
  // buyer's next real ZIP-changing action re-triggers
  // `/calculate-estimated-tax`.
  walletZipTaxResyncFailed: 'Wallet ZIP tax BE-session resync failed',
  // Google Pay sheet-callback funnel (CALLBACK_TIMED_OUT diagnostics) —
  // unambiguous defects, unlike the Warn-level `googlePaySheetStalled`:
  //   - `googlePayDuplicateClick`: a click arrived while a previous
  //     `loadPaymentData` request is still pending. Resolving it would issue a
  //     second request and crash with Google's "This method can only be called
  //     one at a time" (647 occurrences/30d in production, 99% Android). The
  //     duplicate is now rejected (the SDK swallows onClick rejections
  //     silently) and this log records that a pending request existed.
  //   - `googlePaySubmitBlocked`: `onSubmit` fired while a payment was already
  //     in progress. Previously this early-returned WITHOUT settling the
  //     SubmitActions — a guaranteed 30s CALLBACK_TIMED_OUT. The actions are
  //     now rejected (same SDK path as a server decline) and this log records
  //     the occurrence.
  googlePayDuplicateClick: 'Google Pay duplicate click while sheet request pending',
  googlePaySubmitBlocked: 'Google Pay submit while payment already in progress',
};
